import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { ReactComponent as Times } from '@pflegia/ui/icons/times.svg';

import { ModalContainer, ModalSize } from './ModalContainer';
import { usePreventBackgroundScroll } from './usePreventBackgroundScroll';

interface ModalProps {
  children?: ReactNode;
  className?: string;
  onClose?: VoidFunction;
  scrollable?: boolean;
  size?: ModalSize;
  contentWrapperClassName?: string;
  containerClassName?: string;
  /** Classes for the centering wrapper around the modal panel. */
  panelWrapperClassName?: string;
  ariaLabel?: string;
  closeOnEscape?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  children,
  className,
  onClose,
  scrollable,
  size = 'default',
  contentWrapperClassName,
  containerClassName,
  panelWrapperClassName,
  ariaLabel,
  closeOnEscape = false,
}) => {
  const { t } = useTranslation('common');
  usePreventBackgroundScroll();

  useEffect(() => {
    if (!closeOnEscape || !onClose) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOnEscape, onClose]);

  return createPortal(
    <ModalContainer onClose={onClose} size={size} containerClassName={containerClassName} panelWrapperClassName={panelWrapperClassName}>
      <div
        data-testid='modal'
        aria-label={ariaLabel}
        role='dialog'
        aria-modal='true'
        tabIndex={-1}
        className={twMerge(
          clsx(
            'relative inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:px-6 sm:pt-6 sm:align-middle',
            size === 'compact' && 'md:w-225',
            size === 'default' && 'max-w-4xl',
            size === 'large' && 'mx-6 max-w-7xl',
            size === 'full-page' && 'h-full w-full rounded-none px-0 sm:h-auto sm:max-w-4xl sm:rounded-lg sm:px-6',
            className,
          ),
        )}>
        <div
          className={twMerge(
            clsx(
              'flex-1',
              scrollable && 'overflow-y-scroll md:overflow-y-hidden',
              size === 'full-page' && 'top-0 bottom-0 h-full sm:top-auto sm:bottom-auto sm:h-auto',
              contentWrapperClassName,
            ),
          )}>
          {children}
        </div>
        {onClose && (
          <button
            data-testid='close-modal-btn'
            className='focus:ring-main-blue absolute top-0 right-0 block rounded-md p-3 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-offset-2 focus:outline-hidden'
            onClick={onClose}
            type='button'>
            <Times title={t('close')} />
          </button>
        )}
      </div>
    </ModalContainer>,
    document.body,
  );
};

export default Modal;
